<script setup lang="ts">
import { MMIcon, MMMenu } from '@mm-frontend/mm-web-ui-kit';
import CounterTooltip from './CounterTooltip.vue';
import { onClickOutside } from '@vueuse/core';

const props = withDefaults(
  defineProps<{
    value: number;
    max: number;
    min?: number;
    size?: 's36' | 'xl56';
  }>(),
  { min: 0, size: 'xl56' }
);

const emits = defineEmits<{
  (e: 'change', value: number);
  (e: 'focus:change', value: boolean);
}>();

const isMinusAvailable = computed(() => props.value > props.min);
const isPlusAvailable = computed(() => props.value < props.max);

const isPlusHovered = ref(false);
const isTooltipShown = computed(() => isPlusHovered.value && props.value === props.max);

const inputEl = ref();
const counterEl = ref();

function onChange(amount: number | string) {
  // clear-all case
  if (amount === '') {
    emits('change', 0);
    setShownValue(0);
    return;
  }

  // not a digit
  const reg = /^\d+$/;

  if (!reg.test(`${amount}`)) {
    setShownValue(props.value);
    return;
  }

  const newVal = Math.min(Math.max(+amount, props.min), props.max);

  setShownValue(newVal);
  if (newVal == props.value) return;
  emits('change', newVal);
}

function setShownValue(value: number | string) {
  if (!inputEl.value) return;

  inputEl.value.value = `${value}`;
}

const iconSize = computed(() => {
  if (props.size === 'xl56') return 42;
  if (props.size === 's36') return 16;
  return 36;
});

onClickOutside(counterEl, () => emits('focus:change', false));
</script>

<template>
  <div ref="counterEl" class="counter" :class="`counter_${size}`">
    <div
      class="counter__minus"
      :class="{ disabled: !isMinusAvailable }"
      @click="onChange(value - 1)"
    >
      <MMIcon name="minus" color="secondary" :height="iconSize" />
    </div>
    <input
      ref="inputEl"
      :value="value"
      data-test-id="input__quantity"
      @input="onChange(($event.target as HTMLInputElement).value)"
      @focus="$emit('focus:change', true)"
    />

    <div
      class="counter__plus-wrapper"
      @mouseenter="isPlusHovered = true"
      @mouseleave="isPlusHovered = false"
    >
      <MMMenu v-model="isTooltipShown" tail position="top">
        <template #content>
          <CounterTooltip :max="max" class="counter__plus-tooltip" />
        </template>

        <div
          class="counter__plus"
          :class="{ disabled: !isPlusAvailable }"
          @click="onChange(value + 1)"
        >
          <MMIcon name="plus" color="secondary" :height="iconSize" />
        </div>
      </MMMenu>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
.counter
  align-items center
  justify-content space-between
  background-color #fff
  box-shadow inset 0 0 0 1px hsla(220, 4%, 48%, .3)
  color var(--text-primary)
  padding 0 4px
  transition all .15s ease-in-out
  width fit-content;
  display flex
  border-radius 10px
  user-select none

  &_s36
    height 36px

    & input
      font-size 14px
      line-height 18px
      min-width 48px
      width 48px

    .counter__minus, .counter__plus
      height 36px

  &_xl56
    height 56px

    & input
      font-size 18px
      line-height 24px
      min-width 48px
      width 48px

    .counter__minus, .counter__plus
      height 42px

  & input
    outline none
    border none
    text-align center
    font-weight 600
    letter-spacing -0.007em
    color Gray(DK40)
    -moz-appearance textfield

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button
      -webkit-appearance none
      margin 0

  &__minus, &__plus
    cursor pointer
    z-index 1
    display flex
    align-items center
    justify-content center
    aspect-ratio 1
    position relative

    &.disabled
      opacity .4
      pointer-events none

.counter__plus-wrapper
  position relative
</style>
